import { Grid } from './Grid';
import { Cell, Rect, rectIntersect } from './types';

/**
 * A per-cell selection mask.
 *
 * A mask (rather than a rect) is what lets "select by colour", "select by
 * character", magic-wand and lasso all feed the same downstream operations.
 * An empty mask means "no selection", which tools read as "whole layer".
 */
export class Selection {
  readonly width: number;
  readonly height: number;
  private mask: Uint8Array;
  private count = 0;
  private bounds: Rect | null = null;
  private boundsDirty = false;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.mask = new Uint8Array(width * height);
  }

  get isEmpty(): boolean {
    return this.count === 0;
  }

  get size(): number {
    return this.count;
  }

  has(x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return false;
    return this.mask[y * this.width + x] === 1;
  }

  /** Tools call this: with no selection everything is editable. */
  allows(x: number, y: number): boolean {
    return this.count === 0 || this.has(x, y);
  }

  add(x: number, y: number): void {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return;
    const i = y * this.width + x;
    if (this.mask[i] === 0) {
      this.mask[i] = 1;
      this.count += 1;
      this.boundsDirty = true;
    }
  }

  remove(x: number, y: number): void {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return;
    const i = y * this.width + x;
    if (this.mask[i] === 1) {
      this.mask[i] = 0;
      this.count -= 1;
      this.boundsDirty = true;
    }
  }

  clear(): void {
    this.mask.fill(0);
    this.count = 0;
    this.bounds = null;
    this.boundsDirty = false;
  }

  setRect(r: Rect): void {
    this.clear();
    this.addRect(r);
  }

  addRect(r: Rect): void {
    const clipped = rectIntersect(r, { x: 0, y: 0, width: this.width, height: this.height });
    if (!clipped) return;
    for (let y = clipped.y; y < clipped.y + clipped.height; y++) {
      for (let x = clipped.x; x < clipped.x + clipped.width; x++) this.add(x, y);
    }
  }

  subtractRect(r: Rect): void {
    for (let y = r.y; y < r.y + r.height; y++) {
      for (let x = r.x; x < r.x + r.width; x++) this.remove(x, y);
    }
  }

  invert(): void {
    for (let i = 0; i < this.mask.length; i++) this.mask[i] = this.mask[i] === 1 ? 0 : 1;
    this.count = this.mask.length - this.count;
    this.boundsDirty = true;
  }

  selectAll(): void {
    this.mask.fill(1);
    this.count = this.mask.length;
    this.bounds = { x: 0, y: 0, width: this.width, height: this.height };
    this.boundsDirty = false;
  }

  /** Bounding rectangle of the mask, or null when empty. */
  getBounds(): Rect | null {
    if (this.count === 0) return null;
    if (!this.boundsDirty && this.bounds) return this.bounds;
    let minX = this.width;
    let minY = this.height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.mask[y * this.width + x] === 0) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    this.bounds = maxX < 0 ? null : { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
    this.boundsDirty = false;
    return this.bounds;
  }

  clone(): Selection {
    const s = new Selection(this.width, this.height);
    s.mask.set(this.mask);
    s.count = this.count;
    s.boundsDirty = true;
    return s;
  }

  translate(dx: number, dy: number): Selection {
    const s = new Selection(this.width, this.height);
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.mask[y * this.width + x] === 1) s.add(x + dx, y + dy);
      }
    }
    return s;
  }

  forEach(fn: (x: number, y: number) => void): void {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.mask[y * this.width + x] === 1) fn(x, y);
      }
    }
  }

  /** Cells matching a predicate, e.g. "same foreground colour". */
  static fromPredicate(grid: Grid, predicate: (cell: Cell, x: number, y: number) => boolean): Selection {
    const s = new Selection(grid.width, grid.height);
    grid.forEachCell((cell, x, y) => {
      if (predicate(cell, x, y)) s.add(x, y);
    });
    return s;
  }

  /** 4-connected flood region of cells that match the seed (magic wand). */
  static connectedRegion(
    grid: Grid,
    startX: number,
    startY: number,
    match: (a: Cell, b: Cell) => boolean,
  ): Selection {
    const s = new Selection(grid.width, grid.height);
    if (!grid.inBounds(startX, startY)) return s;
    const seed = grid.get(startX, startY);
    const seen = new Uint8Array(grid.width * grid.height);
    const stack: number[] = [startY * grid.width + startX];
    while (stack.length) {
      const i = stack.pop() as number;
      if (seen[i]) continue;
      seen[i] = 1;
      const x = i % grid.width;
      const y = (i - x) / grid.width;
      if (!match(grid.get(x, y), seed)) continue;
      s.add(x, y);
      if (x > 0) stack.push(i - 1);
      if (x < grid.width - 1) stack.push(i + 1);
      if (y > 0) stack.push(i - grid.width);
      if (y < grid.height - 1) stack.push(i + grid.width);
    }
    return s;
  }

  /** Point-in-polygon mask for the lasso tool (even-odd rule on cell centres). */
  static fromPolygon(width: number, height: number, points: ReadonlyArray<{ x: number; y: number }>): Selection {
    const s = new Selection(width, height);
    if (points.length < 3) return s;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const p of points) {
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    const y0 = Math.max(0, Math.floor(minY));
    const y1 = Math.min(height - 1, Math.ceil(maxY));
    for (let y = y0; y <= y1; y++) {
      const cy = y + 0.5;
      for (let x = 0; x < width; x++) {
        const cx = x + 0.5;
        let inside = false;
        for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
          const pi = points[i];
          const pj = points[j];
          const yi = pi.y + 0.5;
          const yj = pj.y + 0.5;
          if (yi > cy !== yj > cy) {
            const xAt = ((pj.x + 0.5 - (pi.x + 0.5)) * (cy - yi)) / (yj - yi) + pi.x + 0.5;
            if (cx < xAt) inside = !inside;
          }
        }
        if (inside) s.add(x, y);
      }
    }
    return s;
  }
}
